import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import { Avatar, Grid, Paper, Stack, TableContainer, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { format } from "date-fns";
import CardHeaderTitle from "../../components/card-header-title/index.jsx";
import AppBarChart from "../../components/chart/bar.jsx";
import AppPieChart from "../../components/chart/pie.jsx";
import WPMChartComponent from "../../components/chart/wpm-chart.js";
import { AppDataContext } from "../../components/context/app-data-context.jsx";
import InfoCardBox from "../../components/info-card-box/index.js";
import ProgressTable from "../../components/progress-table/index.jsx";
import AppSeeMoreLess from "../../components/see-more-less/index.jsx";
import { useThemeMode } from "../../components/theme/index.jsx";
import { DATE_FORMAT, SENTIMENTS_THRESHOLD, THEME_MODES } from "../../helpers/constant.js";
import { capitalize, getDurationString, removeDetectKeywordFromVisuals } from "../../helpers/index.js";
import Utils from "../../utils";
import VIcon from "../images/Logo_black.png";
import { toggleButtonStyle } from "../../consts.js";

function ConversationInsight({ conversationId, alertsData }) {
    const AppDataContextDTO = useContext(AppDataContext);

    const [conversation, setConversation] = useState({});
    const [summary, setSummary] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [agentSpeaker, setAgentSpeaker] = useState("");
    const [clientSpeaker, setClientSpeaker] = useState("");

    const [analytics, setAnalytics] = useState({
        duration: "0",
        agent: "",
        vs: "0",
        date: "",
    });
    const [sentiment, setSentiment] = useState([]);

    const [pickedAgent, setPickedAgent] = React.useState(true);
    const [pickedInsightAgent, setPickedInsightAgent] = React.useState("Agent");
    const { colours, themeMode, FONT } = useThemeMode();

    function calcSentiment(input) {
        let pos = 0;
        let neg = 0;
        let neutral = 0;

        input.forEach((z) => {
            if (z.sentiment) {
                if (z.sentiment.polarity.score < -SENTIMENTS_THRESHOLD) neg += 1;
                else if (z.sentiment.polarity.score > SENTIMENTS_THRESHOLD) pos += 1;
                else neutral += 1;
            }
        });
        return { pos, neg, neutral };
    }

    const replaceSpeakers = (z, speaker1Label, speaker2Label) => {
        if (z.text) {
            z.text = z.text
                .replace(/Speaker 1/gi, speaker1Label) // Replace "Speaker 1" case-insensitively
                .replace(/Speaker 2/gi, speaker2Label); // Replace "Speaker 2" case-insensitively
        }
    };

    const getUniqueNames = (items) => {
        const uniqueNames = [...new Set(items.map(item => item.name))];
        return uniqueNames;
    }

    // removed the fetch and uses a hook to show the alerts
    useEffect(() => {
        console.log("alertsData ", alertsData)
        setAlerts(getUniqueNames(alertsData));
    }, [alertsData]);

    /**
     * Get trackers by type and unique name
     */
    const groupTrackersDetected = (list, type) => {
        const groupedTrackers = new Map();

        list.filter(s => s.for === type).forEach((tracker) => {
            if (!groupedTrackers.has(tracker.name)) {
                groupedTrackers.set(tracker.name, []);
            }
            groupedTrackers.get(tracker.name).push(tracker);
        });

        return Array.from(groupedTrackers.entries()).map(([name, matches]) => ({
            name,
            matches,
            for: type
        }));
    }

    async function getConversationData() {
        const response = await Utils.genericGet("getConversation", conversationId);
        const conversationData = response.data.getConversation;
        try {
            // set data for breadcrumb
            AppDataContextDTO.setCampaign({
                id: conversationData.campaign.id,
                name: conversationData.campaign.name
            });
        } catch (error) { }

        conversationData.analytics = JSON.parse(conversationData.analytics);
        conversationData.questions = JSON.parse(conversationData.questions) || [];
        conversationData.summary = JSON.parse(conversationData.summary || "{}");
        const topicsData = JSON.parse(conversationData.topics) || [];
        topicsData.forEach(t => {
            if (!t.messageIds) {
                t.messageIds = [t.text]
            }
        });
        conversationData.topics = [...topicsData];

        conversationData.transcript = JSON.parse(conversationData.transcript) || [];
        const trackersDetected = JSON.parse(conversationData.trackersdetected);

        const agentInsight = groupTrackersDetected(trackersDetected, 'Agent');
        const customerInsight = groupTrackersDetected(trackersDetected, 'Customer');

        conversationData.trackersdetected = [...agentInsight, ...customerInsight];

        let agentTag = conversationData.agenttag;

        if (!agentTag || (agentTag !== "Speaker 1" && agentTag !== "Speaker 2")) {
            console.warn("Agent tag is missing or invalid. Defaulting to 'Speaker 2' as the agent.");
            agentTag = "Speaker 2"; // Default to Speaker 2 as the agent
        }

        // Dynamically assign agent and client roles based on agentTag
        setAgentSpeaker(agentTag); // Agent is identified by the tag
        setClientSpeaker(agentTag === "Speaker 1" ? "Speaker 2" : "Speaker 1");

        let totalsentiment = conversationData.transcript.messages.length;

        setSentiment([
            {
                name: "Negative",
                Negative: (
                    (conversationData.transcript.messages.filter(
                        (z) => (z.sentiment ? z.sentiment.polarity.score <= -SENTIMENTS_THRESHOLD : false)
                    ).length /
                        totalsentiment) *
                    100
                ).toFixed(0),
            },
            {
                name: "Neutral",
                Neutral: (
                    ((totalsentiment -
                        conversationData.transcript.messages.filter(
                            (z) => (z.sentiment ? z.sentiment.polarity.score >= SENTIMENTS_THRESHOLD : false)
                        ).length -
                        conversationData.transcript.messages.filter(
                            (z) => (z.sentiment ? z.sentiment.polarity.score <= -SENTIMENTS_THRESHOLD : false)
                        ).length) /
                        totalsentiment) *
                    100
                ).toFixed(0),
            },
            {
                name: "Positive",
                Positive: (
                    (conversationData.transcript.messages.filter(
                        (z) => (z.sentiment ? z.sentiment.polarity.score >= SENTIMENTS_THRESHOLD : false)
                    ).length /
                        totalsentiment) *
                    100
                ).toFixed(0),
            },
        ]);
        let total_talk_time = conversationData.analytics.metrics.find((z) => z.type === "total_talk_time").seconds;
        
        const duration = getDurationString(total_talk_time);

        const date = conversationData.startdate;
        const agent = conversationData.agent;
        const vs = (conversationData.VS || 0) + "%";

        setAnalytics({ date, vs, agent, duration });
        setConversation(conversationData);

        const newSummary = conversationData.summary.summary || [];
        newSummary.forEach((z) => {
            if (z.text.includes("Speaker 0") && agentTag === "Speaker 1") {
                z.text = z.text.replace(/Speaker 0/gi, agent);
            } else if (z.text.includes("Speaker 1") && agentTag === "Speaker 2") {
                z.text = z.text.replace(/Speaker 1/gi, agent);
            }  
        });
        const agent_tag = conversationData.agenttag;

        try {
            if (Array.isArray(newSummary)) {
                if (agent_tag === "Speaker 1") {
                    newSummary.forEach((z) => {
                        replaceSpeakers(z, "Agent", "Client");
                    });
                } else {
                    newSummary.forEach((z) => {
                        replaceSpeakers(z, "Client", "Agent");
                    });
                }
            }
        } catch (error) {
            console.log("Error processing summary", error);
        }

        setSummary(newSummary);
    }

    React.useEffect(() => {
        setAnalytics({
            duration: "0",
            agent: "",
            vs: "0",
            date: "",
        });
        setSentiment([]);
        setAlerts(getUniqueNames(alertsData));
        setSummary([]);
        setConversation({});
        setAgentSpeaker("");
        setClientSpeaker("");
        setPickedAgent(true);
        setPickedInsightAgent("Agent");
        getConversationData();
    }, [conversationId]);

    const safeSummary = Array.isArray(summary) ? summary : [];

    const summaryText = safeSummary.map((z) => z.text).join(" ") || "";

    // Cards Data
    const cardsData = [
        {
            icon: "user",
            title: "Agent",
            value: analytics.agent,
            backgroundColor: colours.blue10,
            borderColor: colours.blue20,
            iconBg: colours.blue100,
        },
        {
            icon: "calendar",
            title: "Date",
            value: analytics?.date ? format(new Date(analytics?.date), DATE_FORMAT) : "",
            backgroundColor: colours.purple5,
            borderColor: colours.purple20,
            iconBg: colours.purple100,
        },
        {
            icon: "watch",
            title: "Call Duration",
            value: analytics.duration,
            backgroundColor: colours.success5,
            borderColor: colours.success20,
            iconBg: colours.success100,
        },
        {
            customAvatar: <Avatar sx={(theme) => ({ bgcolor: theme.palette.mode === THEME_MODES.LIGHT ? colours.white : colours.black })}><img src={VIcon} alt="Vocalysd Avatar" loading="lazy" width={18} height={22} /></Avatar>,
            title: "Avg. Vocalysd Score",
            customValue: <Typography variant="h5" component="div" fontWeight={600} color={colours.success100}>{analytics.vs}</Typography>,
            backgroundColor: colours.orange10,
            borderColor: colours.orange20,
        },
    ];

    // Determines color based on sentiment
    const getSentimentColor = (item) => {
        if (item?.sentiment === "positive") return "success";
        if (item?.sentiment === "negative") return "warning";
        return "inherit";
    };

    return (
        <Grid
            className="vocalysed-home-upadted conversation-insights"
            container
            spacing={0}
            direction="row"
            flexGrow={1}
            padding={3}
            gap={3}
            overflow="auto"
            alignItems="start"
            sx={{ overflowX: "hidden" }}
        >
            {/* Top Cards Section */}
            <Grid
                item
                xs={12}
                padding={3}
                sx={{ border: `1px solid ${colours.grayDark500}`, borderRadius: 2.5, boxShadow: "none", backgroundColor: colours.white }}
                component={Paper}
            >
                <Grid
                    container
                    spacing={3}
                    direction="row"
                >
                    {cardsData.map((card, index) => (
                        <Grid key={index} item xs={12} sm={6} lg={3}>
                            <InfoCardBox
                                colours={colours}
                                themeMode={themeMode}
                                icon={card.icon}
                                title={card.title}
                                tooltip={card.tooltip}
                                value={card.value}
                                backgroundColor={card.backgroundColor}
                                borderColor={card.borderColor}
                                iconBg={card.iconBg}
                                customAvatar={card.customAvatar}
                                customValue={card.customValue}
                            />
                        </Grid>
                    ))}
                    {summaryText.length > 0 &&
                        <Grid item xs={12}>
                            <Typography variant="body2" fontWeight={600} gutterBottom>Conversation Summary</Typography>
                            <Typography variant="body2" sx={{ fontSize: FONT.SIZE_13 }}>
                                <AppSeeMoreLess clampLines={2}>
                                    {summaryText}
                                </AppSeeMoreLess>
                            </Typography>
                        </Grid>
                    }
                </Grid>
            </Grid>

            <Grid
                container
                spacing={0}
                direction="row"
                gap={3}
            >
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="smile"
                                title="Sentiment"
                                tooltip="Sentiment graph showing the overall sentiment trends for this campaign"
                            />

                            <Box marginTop={2} height={150}>
                                <AppBarChart
                                    data={sentiment}
                                />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="alert-triangle"
                                title="Alerts"
                                tooltip="Campaign alerts showing critical updates and notifications"
                            />

                            <Box marginTop={2}>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    {alerts.length > 0
                                        ? alerts.map((alert, index) => (
                                            <Chip
                                                key={index}
                                                label={removeDetectKeywordFromVisuals(alert)}
                                                size="small"
                                                color="warning"
                                            />
                                        ))
                                        : null // Fallback message if alerts is empty or undefined
                                    }
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>

                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={.5}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <CardHeaderTitle
                            icon="activity"
                            title="Insights"
                            tooltip="Insights highlighting key phrases, topics, and trends detected in agent-client interactions"
                        />
                            <ButtonGroup
                                variant="contained"
                                aria-label="outlined primary button group"
                                className="buttons-border-extend"
                            >
                                <Button
                                    style={toggleButtonStyle(pickedInsightAgent === "Agent", colours, themeMode)}
                                    onClick={() => setPickedInsightAgent("Agent")}
                                >
                                    Agent
                                </Button>
                                <Button
                                    style={toggleButtonStyle(pickedInsightAgent === "Customer", colours, themeMode)}
                                    onClick={() => setPickedInsightAgent("Customer")}
                                >
                                    Client
                                </Button>
                            </ButtonGroup>
                        </Stack>
                        <ProgressTable
                            data={_.filter((conversation?.trackersdetected || []), { "for": pickedInsightAgent })}
                            filterKey="name"
                            sortKey="matches"
                            maxItems={20}
                            capCase={(val) => capitalize(removeDetectKeywordFromVisuals(val))}
                            getColorFn={() => "warning"}
                            getValueFn={(item) => item.matches.length}
                        />
                    </Paper>
                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >

                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={.5}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Top Questions"
                                tooltip="Top questions identified from campaign conversations, based on frequency"
                            />

                            <ButtonGroup
                                variant="contained"
                                aria-label="outlined primary button group"
                                className="buttons-border-extend"
                            >
                                <Button
                                    onClick={() => setPickedAgent(true)}
                                    style={toggleButtonStyle(pickedAgent, colours, themeMode)}
                                >
                                    Agent
                                </Button>
                                <Button
                                    onClick={() => setPickedAgent(false)}
                                    style={toggleButtonStyle(!pickedAgent, colours, themeMode)}
                                >
                                    Client
                                </Button>

                            </ButtonGroup>
                        </Stack>
                        <Box marginTop={2}>
                            <TableContainer>
                                <Table size="small" aria-label="Top Questions Table">
                                    <TableBody>
                                        {!conversation.questions?.length > 0 ? (
                                            <TableRow />
                                        ) : (
                                            conversation.questions?.filter((z) => pickedAgent ? z.speaker === agentSpeaker : z.speaker === clientSpeaker)
                                                .sort((a, b) => b.n - a.n)
                                                .slice(0, 10)
                                                .map((z, zi) => (
                                                    <TableRow hover key={zi}>
                                                        <TableCell
                                                            style={{
                                                                paddingTop: 3,
                                                                paddingBottom: 3,
                                                            }}
                                                        >
                                                            {zi + 1 + ". " + z.text}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} lg sx={{ minWidth: "1px" }} display="flex" flexDirection="column" gap={3}>
                    <Grid
                        container
                        flexWrap={{ sm: "nowrap" }}
                        justifyContent="space-between"
                        gap={3}
                    >
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="check-circle"
                                title="Avg. WPM Agent vs Client"
                                tooltip="Average words per minute (WPM) comparison between agent and client in this campaign"
                            />

                            <Box marginTop={1} height={150}>
                                <WPMChartComponent type={"conversation"}
                                    agentSpeaker={agentSpeaker}
                                    clientSpeaker={clientSpeaker}
                                    data={conversation.analytics} />
                            </Box>
                        </Paper>
                        <Paper
                            sx={{ display: "flex", flexDirection: "column", width: { xs: '100%', sm: '50%' }, borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                        >
                            <CardHeaderTitle
                                icon="pie-chart"
                                title="Speaker Analytics"
                                tooltip="Speaker analytics providing insights on individual speaker performance"
                            />

                            <Box height={166}>
                                <AppPieChart
                                    data={!conversation.analytics
                                        ? []
                                        : [
                                            {
                                                name: "Agent",
                                                value: conversation.analytics.members.find(
                                                    (a) =>
                                                        a.name === "Speaker 2" ||
                                                        a.name === "unknown_speaker" ||
                                                        a.name === "Speaker 0" ||
                                                        a.name === "Speaker 4" ||
                                                        a.name === "Speaker 6"
                                                )?.talkTime?.seconds || 0,
                                            },
                                            {
                                                name: "Client",
                                                value: conversation.analytics.members.find(
                                                    (a) =>
                                                        a.name === "Speaker 1" ||
                                                        a.name === "Speaker 3" ||
                                                        a.name === "Speaker 5" ||
                                                        a.name === "Speaker 7"
                                                )?.talkTime?.seconds || 0,
                                            },
                                            {
                                                name: "Silence",
                                                value: conversation.analytics.metrics.find(
                                                    (a) => a.type === "total_silence"
                                                )?.seconds,
                                            },
                                        ]}
                                />
                            </Box>
                        </Paper>
                    </Grid>

                    <Paper
                        sx={{ display: "flex", flexDirection: "column", width: '100%', borderRadius: 2.5, padding: 3, boxSizing: "border-box" }}
                    >
                        <CardHeaderTitle
                            icon="message-circle"
                            title="Topics"
                            tooltip="The top topics discussed in this campaign based on conversation analysis"
                        />

                        <ProgressTable
                            data={conversation?.topics?.filter((r) => r.text) || []}
                            filterKey="text"
                            sortKey="messageIds"
                            maxItems={10}
                            capCase={capitalize}
                            getValueFn={(item) => (item.messageIds?.length) || 0}
                            getColorFn={getSentimentColor}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Grid >
    );
}

export default ConversationInsight;
